import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/utils/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Building2, Loader2, Plus, Search } from "lucide-react";

// Enterprise accounts (e.g. Wakefit): a customer account with SKU master, pincode lanes and bulk order upload.
export default function EnterpriseAccounts() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState(null);
  const [adding, setAdding] = useState(false);
  const [q, setQ] = useState("");
  const [found, setFound] = useState([]);
  const [pick, setPick] = useState(null);
  const [days, setDays] = useState(2);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/v2/enterprise/accounts").then((r) => setRows(r.data.items)).catch((e) => { toast.error(e.response?.data?.detail || "Could not load"); setRows([]); });
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (q.trim().length < 2) { setFound([]); return; }
    const t = setTimeout(() => api.get("/v2/enterprise/customer-search", { params: { q } }).then((r) => setFound(r.data.items)).catch(() => {}), 300);
    return () => clearTimeout(t);
  }, [q]);

  const create = async () => {
    if (!pick) { toast.error("Choose the customer first"); return; }
    setBusy(true);
    try { await api.post("/v2/enterprise/accounts", { customer_id: pick.customer_id, promise_days: Number(days) }); toast.success(`${pick.name} is now an enterprise account`); setAdding(false); setPick(null); setQ(""); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not create"); }
    finally { setBusy(false); }
  };

  return (
    <DashboardLayout>
      <div className="max-w-5xl space-y-5" data-testid="enterprise-accounts">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2"><Building2 className="w-6 h-6 text-brand-500" />Enterprise accounts</h1>
            <p className="text-sm text-slate-500">Large B2B customers with their own SKU master, pincode lanes and bulk order upload. Their shipments appear in All Shipments under Channel: Enterprise.</p>
          </div>
          <div className="ml-auto flex gap-2">
            <Link to="/admin/enterprise-routes" className="h-10 px-4 inline-flex items-center rounded-lg border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-50">Routing lanes</Link>
            {isAdmin && <button onClick={() => setAdding((v) => !v)} className="h-10 px-4 inline-flex items-center gap-1 rounded-lg bg-brand-500 text-white text-sm font-semibold" data-testid="add-enterprise"><Plus className="w-4 h-4" />Add account</button>}
          </div>
        </div>

        {adding && (
          <div className="bg-white rounded-xl border border-brand-100 p-5 space-y-3">
            <p className="text-sm font-semibold text-slate-800">Link an existing customer account</p>
            <div className="relative max-w-md">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={q} onChange={(e) => { setQ(e.target.value); setPick(null); }} placeholder="Search company, contact, email or mobile" aria-label="Search customer"
                className="w-full h-10 pl-9 pr-3 border border-slate-300 rounded-lg text-sm" data-testid="ent-customer-search" />
            </div>
            {found.length > 0 && !pick && (
              <ul className="max-w-md border border-slate-200 rounded-lg divide-y divide-slate-100">{found.map((f) => (
                <li key={f.customer_id}><button type="button" onClick={() => setPick(f)} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50">
                  <b>{f.name}</b> <span className="text-slate-500">{f.mobile || ""} {f.email || ""}</span></button></li>))}</ul>)}
            {pick && <p className="text-sm">Selected: <b>{pick.name}</b></p>}
            <div className="flex flex-wrap items-end gap-3">
              <label className="text-xs font-semibold text-slate-600">Default promise (working days)
                <input type="number" min="1" max="10" value={days} onChange={(e) => setDays(e.target.value)} className="mt-1 block w-32 h-10 border border-slate-300 rounded-lg px-3 text-sm" />
              </label>
              <button onClick={create} disabled={busy || !pick} className="h-10 px-4 rounded-lg bg-slate-900 text-white text-sm font-semibold disabled:opacity-50">Create enterprise account</button>
            </div>
          </div>)}

        {!rows ? <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div> : rows.length === 0 ? (
          <div className="bg-white rounded-xl border border-dashed border-slate-300 p-10 text-center text-sm text-slate-500">No enterprise accounts yet.{isAdmin ? " Use Add account to link a customer such as Wakefit." : ""}</div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">{rows.map((a) => (
            <Link key={a.id} to={`/admin/enterprise/${a.id}`} className="block bg-white rounded-xl border border-slate-200 p-5 hover:border-brand-500 hover:shadow-sm transition" data-testid={`ent-${a.id}`}>
              <div className="flex items-center gap-2">
                <p className="font-heading font-bold text-lg text-slate-900">{a.name}</p>
                {a.is_active === false && <span className="text-[10px] font-bold bg-slate-100 text-slate-500 rounded-full px-2 py-0.5">Inactive</span>}
              </div>
              <div className="grid grid-cols-4 gap-2 mt-3 text-center">
                {[["Open", a.stats.open], ["Delivered 30d", a.stats.delivered_30d], ["On time", a.stats.on_time_pct == null ? "–" : `${a.stats.on_time_pct}%`], ["SKUs", a.sku_count]].map(([k, v]) => (
                  <div key={k} className="bg-slate-50 rounded-lg py-2"><p className="text-[10px] uppercase text-slate-500 font-semibold">{k}</p><p className="font-heading font-bold text-slate-900">{v}</p></div>))}
              </div>
              <p className="text-xs text-slate-500 mt-3">Promise {a.promise_days} working day(s) unless a lane says otherwise</p>
            </Link>))}</div>)}
      </div>
    </DashboardLayout>
  );
}
